// Product analytics — PostHog, wrapped so the rest of the console can't misuse it.
//
// Same posture as Guard Web (apps/guard-web/src/lib/analytics.ts; third copy convention like
// live-config — shared-package candidate later, Codex's lane):
//   - The event allowlist below is the ENTIRE vocabulary; unknown props are stripped at runtime.
//     No emails, guard labels, checkpoint/site names, GPS, tokens, or free text can leave.
//   - autocapture, pageview capture, session recording, and the remote /decide config are all OFF
//     at init — on the supervisor console this matters even more: replay would film the live map,
//     rosters, and alerts of client sites.
//   - SDK loads lazily and ONLY when a runtime-injected phc_ key exists. No key = frozen no-op.

export type AnalyticsEventName =
  | "app_booted"
  | "section_viewed"
  | "alert_acknowledged"
  | "report_exported";

/** Per-event prop allowlist — anything else is stripped before it can leave the device. */
// Property names follow the shared mobile contract (apps/mobile mobile-analytics, 6b382ed).
const EVENT_PROPS: Record<AnalyticsEventName, readonly string[]> = {
  app_booted: ["appVersion", "networkOnline"],
  section_viewed: ["section"],
  alert_acknowledged: [],
  report_exported: ["rows", "capped"],
};

/** Structural slice of posthog-js we call — injectable for tests. */
export interface PosthogLike {
  init(key: string, options: Record<string, unknown>): void;
  capture(event: string, props?: Record<string, unknown>): void;
  identify(id: string, props?: Record<string, unknown>): void;
  group?(type: string, key: string): void;
}

export interface Analytics {
  readonly enabled: boolean;
  capture(event: AnalyticsEventName, props?: Record<string, string | number | boolean>): void;
  /** Identify with the opaque Supabase user UUID; accountTail is the last-4 account id chars. */
  identify(userId: string, accountTail: string): void;
}

export const NOOP_ANALYTICS: Analytics = Object.freeze({
  enabled: false,
  capture() {},
  identify() {},
});

async function defaultLoader(): Promise<PosthogLike> {
  const mod = await import("posthog-js");
  return mod.default as unknown as PosthogLike;
}

export function createAnalytics(options: {
  key: string | undefined;
  host: string | undefined;
  /** Which product surface these events come from — one PostHog project serves all surfaces. */
  surface: "guard-web" | "console";
  loader?: () => Promise<PosthogLike>;
}): Analytics {
  const { key, host, surface } = options;
  if (key === undefined) {
    return NOOP_ANALYTICS;
  }
  const loader = options.loader ?? defaultLoader;

  let client: PosthogLike | null = null;
  let loadStarted = false;
  const pending: Array<(ph: PosthogLike) => void> = [];

  function run(action: (ph: PosthogLike) => void): void {
    if (client !== null) {
      action(client);
      return;
    }
    pending.push(action);
    if (loadStarted) {
      return;
    }
    loadStarted = true;
    void loader()
      .then((ph) => {
        ph.init(key as string, {
          api_host: host ?? "https://us.i.posthog.com",
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: false,
          disable_session_recording: true,
          advanced_disable_decide: true,
          persistence: "localStorage",
          person_profiles: "identified_only",
        });
        client = ph;
        for (const action of pending.splice(0)) {
          action(ph);
        }
      })
      .catch(() => {
        // SDK failed to load (offline, blocked) — analytics silently stays off this session.
        pending.length = 0;
      });
  }

  return {
    enabled: true,
    capture(event, props) {
      const allowed = EVENT_PROPS[event];
      const safe: Record<string, string | number | boolean> = { surface };
      for (const prop of allowed) {
        const value = props?.[prop];
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
          safe[prop] = value;
        }
      }
      run((ph) => ph.capture(event, safe));
    },
    identify(userId, accountTail) {
      run((ph) => {
        ph.identify(userId, { account_tail: accountTail });
        ph.group?.("account", accountTail);
      });
    },
  };
}
